import type { StatusTone } from '../../theme';
import { getStatusMeta } from '../../utils/statusMeta';

/**
 * Project statuses in the order a project moves through them, so the status
 * breakdown reads top to bottom like a project's life. Statuses outside this
 * list (legacy or new values) follow, largest first.
 */
const WORKFLOW_ORDER = [
  'VIDEO_SUBMITTED',
  'EDITOR_ASSIGNED',
  'REQUIREMENTS_REVIEW',
  'EDITING_IN_PROGRESS',
  'REVISION_REQUESTED',
  'DRAFT_READY',
  'CLIENT_REVIEW',
  'FINAL_APPROVAL',
  'DELIVERED',
  'CANCELLED',
];

export interface StatusRow {
  /** Sentence-case label from statusMeta ("Editing in progress"). */
  label: string;
  tone: StatusTone;
  count: number;
}

const workflowIndexFor = (label: string): number =>
  WORKFLOW_ORDER.findIndex((status) => getStatusMeta('project', status).label === label);

/**
 * One row per status label, in workflow order. Raw values that read the same
 * (a legacy "COMPLETED" and "DELIVERED" are both "Delivered") are merged, so
 * a label never appears twice. Empty and non-positive counts are dropped.
 */
export const buildStatusRows = (entries: Array<{ status: string; count: number }>): StatusRow[] => {
  const byLabel = new Map<string, StatusRow>();
  for (const entry of entries) {
    const count = Number(entry?.count) || 0;
    if (count <= 0) continue;
    const meta = getStatusMeta('project', entry.status);
    const existing = byLabel.get(meta.label);
    if (existing) {
      existing.count += count;
    } else {
      byLabel.set(meta.label, { label: meta.label, tone: meta.tone, count });
    }
  }
  return Array.from(byLabel.values()).sort((a, b) => {
    const ai = workflowIndexFor(a.label);
    const bi = workflowIndexFor(b.label);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return b.count - a.count;
  });
};

export interface MonthLabel {
  /** "May" */
  month: string;
  /** "2026" */
  year: string;
}

/**
 * "2026-05" -> { month: "May", year: "2026" }. The API buckets projects by UTC
 * month, so the label is read in UTC too: in the viewer's own timezone, west
 * of UTC, midnight on May 1 UTC is still April.
 */
export const formatMonthKey = (key: string): MonthLabel | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(key || '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  const date = new Date(Date.UTC(year, monthIndex, 1));
  return {
    month: date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }),
    year: String(year),
  };
};

/** 64.3 -> "64.3%", 50 -> "50%". Clamped to 0-100. */
export const formatPercent = (value: number): string => {
  const safe = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
  return `${Number(safe.toFixed(1))}%`;
};

/** Share of a total as a whole percentage, for bar widths and labels. */
export const shareOf = (count: number, total: number): number =>
  total > 0 ? Math.round((Math.max(0, count) / total) * 100) : 0;
